/**
 * hw-cart-free-shipping-gifts.js
 *
 * Manages two features for the hw-cart-free-shipping-gifts cart block:
 *
 *   A. Free Shipping Bar — progress bar is driven entirely by Liquid-rendered
 *      CSS custom properties; no JS needed beyond accessibility.
 *
 *   B. Free Gifts — evaluates gift thresholds after every cart:update (section
 *      re-render) and auto-adds eligible products to the cart.
 *
 * Architecture:
 *  - The Liquid snippet renders hidden [data-hw-gift-slot] elements for each
 *    product slot, embedding unlocked/in-cart state as data-* attributes.
 *  - After section re-render (post cart:update morph), this script scans
 *    the fresh DOM and fires /cart/add.js for any unlocked-but-not-in-cart
 *    product slots (always auto mode).
 *  - A "pending" Set prevents double-firing during the add → re-render loop.
 */

// ── Constants ─────────────────────────────────────────────────────────────────

const CART_UPDATE_EVENT = 'cart:update';
const GIFT_SLOT_SEL     = '[data-hw-gift-slot]';
const GIFTS_WRAP_SEL    = '[data-hw-gifts]';

// Variant IDs currently being added; prevents duplicate POSTs during re-render
/** @type {Set<string>} */
const pendingAdds = new Set();

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Gather section IDs for Shopify section rendering (same pattern as hw-empty-cart.js).
 * @returns {string}
 */
function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

/**
 * Add a variant to the cart and fire cart:update when done.
 * @param {string} variantId
 * @param {number} [qty=1]
 */
function addGiftToCart(variantId, qty = 1) {
  if (pendingAdds.has(variantId)) return;
  pendingAdds.add(variantId);

  const sectionIds = getCartSectionIds();
  const payload = {
    id: parseInt(variantId, 10),
    quantity: qty,
    properties: { _hw_gift: '1' },
    ...(sectionIds && { sections: sectionIds }),
  };

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then((r) => r.json())
    .then((response) => {
      document.dispatchEvent(
        new CustomEvent(CART_UPDATE_EVENT, {
          bubbles: true,
          detail: { data: { sections: response.sections ?? {} } },
        })
      );
    })
    .catch((err) => {
      console.error('[hw-cart-free-shipping-gifts] Gift add failed:', err);
    })
    .finally(() => {
      pendingAdds.delete(variantId);
    });
}

// ── Core evaluation ───────────────────────────────────────────────────────────

/**
 * Scan all gift slots in the DOM.
 * For auto mode: add any unlocked, not-in-cart gifts.
 * Called after section re-render so data-* attributes are fresh.
 */
function evaluateGiftSlots() {
  document.querySelectorAll(GIFT_SLOT_SEL).forEach((slot) => {
    const wrap = slot.closest(GIFTS_WRAP_SEL);
    if (!wrap) return;

    const mode      = wrap.dataset.giftMode ?? 'auto';
    const unlocked  = slot.dataset.unlocked === 'true';
    const inCart    = slot.dataset.inCart === 'true';
    const variantId = slot.dataset.variantId;

    if (!variantId || !unlocked || inCart) return;

    if (mode === 'auto') {
      addGiftToCart(variantId);
    }
  });
}

// ── Event wiring ──────────────────────────────────────────────────────────────

/**
 * After cart:update, Horizon's cart-items-component morphs the DOM asynchronously.
 * We defer our check slightly so the fresh Liquid-rendered data-* attributes are
 * in place when we read them.
 */
document.addEventListener(CART_UPDATE_EVENT, () => {
  // Two-frame defer: first rAF lets the morph start; second lets paint settle.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      evaluateGiftSlots();
    });
  });
});

// ── Initial scan ──────────────────────────────────────────────────────────────

// Run once on load in case the cart already meets thresholds when the page opens.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', evaluateGiftSlots);
} else {
  evaluateGiftSlots();
}
